import { customAlphabet } from "nanoid";
import { createElementId } from "shared/util/utils";
import { FileMetadata, FileMetadataType, FileUploadState } from "shared/datamodel/schemas/metadata";

export function fileExt(filename: string) {
  const i = filename.lastIndexOf(".");
  return i == -1 ? "" : filename.substring(i + 1);
}

const filenameGen = customAlphabet("0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ", 12);

const fileUrlRegex = /^(.*)\/(.*)\/(.*)/;

export function buildUploadUrl(accountId: string, documentId: string, filename: string) {
  return `${accountId}/${documentId}/${filenameGen()}-${Date.now()}.${fileExt(filename)}`;
}

export function buildMetadataEntry(
  filename: string,
  filetype: string,
  url: string | null,
  userId: string | null | undefined,
  limitedslot: number = -1,
  id?: string,
  uploadState: FileUploadState = FileUploadState.Completed
) {
  return {
    type: FileMetadataType,
    id: id ?? createElementId(),
    fileName: filename,
    url: url ?? "",
    limitedSlot: limitedslot,
    fileType: filetype,
    uploadedBy: userId,
    uploadState,
    uploadProgress: 1,
    lastModifiedTimestamp: 0,
  } as FileMetadata;
}

function createReplacerDocumentId(newDocumentId: string, newAccountId?: string) {
  return (_match: string, p1: string, _p2: string, p3: string) => `${newAccountId ?? p1}/${newDocumentId}/${p3}`;
}

export function prepareCopyForeignImages({
  metadata,
  documentId,
  fileKeyPrefix = FileMetadataType,
  accountId,
}: {
  metadata: [string, any][];
  documentId: string;
  fileKeyPrefix?: string;
  accountId?: string;
}): Record<string, string> {
  const replaceUrlFn = createReplacerDocumentId(documentId, accountId);

  return metadata
    .filter(
      ([key, value]) =>
        key.startsWith(fileKeyPrefix) && // find entries that are files
        value && // guard against undefined values: happened once due to bug elsewhere in the code
        !value.url.startsWith("data:") && // and are not data urls
        !value.url.includes(documentId) // and the url does not contain the document id of our document
    )
    .reduce((acc, cur) => {
      const [, value] = cur;
      const newUrl = value.url.replace(fileUrlRegex, replaceUrlFn); // replace the document id in the url to ours
      return { ...acc, [value.url]: newUrl };
    }, {});
}
